// ============================================================
// FYUNOR — US State Income Tax
// State-specific tax data used by the Salary Calculator.
// Federal tax and FICA live in the shared @/lib/us modules.
// ============================================================

import { calcProgressiveTax } from "@/lib/us/tax";
import { FILING_STATUS } from "@/lib/us/constants";

// States with no income tax on wages.
export const NO_TAX_STATES = new Set([
  "AK", "FL", "NV", "NH", "SD", "TN", "TX", "WA", "WY",
]);

// Flat-rate state estimates. Review against the latest published state rules before each annual release.
export const FLAT_RATE = {
  AZ: 0.025, AR: 0.038, CO: 0.044, GA: 0.0539, IA: 0.038,
  IL: 0.0495, IN: 0.0275, KY: 0.04, MA: 0.05, MI: 0.0425,
  MS: 0.044, NC: 0.0425, ND: 0.025, PA: 0.0307, UT: 0.0455,
};

// Detailed state bracket estimates (Single + MFJ) for selected states.
export const STATE_BRACKETS = {
  CA: {
    single: {
      deduction: 5540,
      brackets: [
        { rate: 0.01, upTo: 10456 },
        { rate: 0.02, upTo: 24812 },
        { rate: 0.04, upTo: 39156 },
        { rate: 0.06, upTo: 54344 },
        { rate: 0.08, upTo: 68660 },
        { rate: 0.093, upTo: 350612 },
        { rate: 0.103, upTo: 419705 },
        { rate: 0.113, upTo: 698274 },
        { rate: 0.123, upTo: Infinity },
      ],
    },
    mfj: {
      deduction: 11080,
      brackets: [
        { rate: 0.01, upTo: 20912 },
        { rate: 0.02, upTo: 49624 },
        { rate: 0.04, upTo: 78312 },
        { rate: 0.06, upTo: 108688 },
        { rate: 0.08, upTo: 137320 },
        { rate: 0.093, upTo: 701224 },
        { rate: 0.103, upTo: 839410 },
        { rate: 0.113, upTo: 1396548 },
        { rate: 0.123, upTo: Infinity },
      ],
    },
  },
  NY: {
    single: {
      deduction: 8000,
      brackets: [
        { rate: 0.04, upTo: 8500 },
        { rate: 0.045, upTo: 11700 },
        { rate: 0.0525, upTo: 13900 },
        { rate: 0.055, upTo: 80650 },
        { rate: 0.0585, upTo: 215400 },
        { rate: 0.0625, upTo: 1077550 },
        { rate: 0.0685, upTo: Infinity },
      ],
    },
    mfj: {
      deduction: 16050,
      brackets: [
        { rate: 0.04, upTo: 17150 },
        { rate: 0.045, upTo: 23600 },
        { rate: 0.0525, upTo: 27900 },
        { rate: 0.055, upTo: 161550 },
        { rate: 0.0585, upTo: 323200 },
        { rate: 0.0625, upTo: 2155350 },
        { rate: 0.0685, upTo: Infinity },
      ],
    },
  },
  NJ: {
    single: {
      deduction: 1000,
      brackets: [
        { rate: 0.014, upTo: 20000 },
        { rate: 0.0175, upTo: 35000 },
        { rate: 0.035, upTo: 40000 },
        { rate: 0.05525, upTo: 75000 },
        { rate: 0.0637, upTo: Infinity },
      ],
    },
    mfj: {
      deduction: 2000,
      brackets: [
        { rate: 0.014, upTo: 20000 },
        { rate: 0.0175, upTo: 50000 },
        { rate: 0.0245, upTo: 70000 },
        { rate: 0.035, upTo: 80000 },
        { rate: 0.05525, upTo: 150000 },
        { rate: 0.0637, upTo: Infinity },
      ],
    },
  },
};

// Simplified effective-rate approximation for remaining progressive states; excludes many state-specific deductions, credits, and local taxes.
export const APPROX_RATE = {
  AL: 0.05, CT: 0.05, DE: 0.055, DC: 0.065, HI: 0.075,
  ID: 0.058, KS: 0.0525, LA: 0.04, MD: 0.05, ME: 0.07,
  MN: 0.065, MO: 0.044, MT: 0.059, NE: 0.052, NM: 0.049,
  OH: 0.035, OK: 0.045, OR: 0.085, RI: 0.0475, SC: 0.06,
  VT: 0.066, VA: 0.0575, WI: 0.055, WV: 0.045,
};

export const STATE_OPTIONS = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "District of Columbia", value: "DC" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

/** Estimate state income tax for a taxable income, filing status, and state. */
export function calcStateTax(taxable, filingStatus, state) {
  if (!state || NO_TAX_STATES.has(state)) return 0;
  if (FLAT_RATE[state] !== undefined) {
    return Math.max(0, taxable) * FLAT_RATE[state];
  }
  const st = STATE_BRACKETS[state];
  if (st) {
    const cfg = filingStatus === FILING_STATUS.MARRIED_JOINTLY ? st.mfj : st.single;
    const stateTaxable = Math.max(0, taxable - cfg.deduction);
    return calcProgressiveTax(stateTaxable, cfg.brackets);
  }
  const rate = APPROX_RATE[state] ?? 0.05;
  return Math.max(0, taxable) * rate;
}